import { useState } from 'react';
import {
  TrendingUp,
  Users,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Gift,
  RefreshCw,
  Search,
  Filter,
  Eye
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import type { IUserFinancialData } from '@/shared/types';
import { useAdminStats } from "./hooks/useAdminStats";
import { useAuthStore } from '@/shared/stores/authStore';
import { useDebounce } from './hooks/useDebounce';


export default function FinancialOverview() {
  const [activeTab, setActiveTab] = useState<'overview' | 'packages' | 'users'>('overview');
  const [currentPage, setCurrentPage] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterPackage, setFilterPackage] = useState('all');
  const [selectedUser, setSelectedUser] = useState<IUserFinancialData | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const { user: authStoreUser, isAuthenticated } = useAuthStore();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data: stats, isLoading } = useAdminStats(debouncedSearch, currentPage, filterPackage);


  if (!isAuthenticated || authStoreUser?.role == "user") {
    window.location.href = '/login';
    return null;
  }



  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentMethodBadge = (method: string) => {
    const badges = {
      cash: 'bg-green-100 text-green-800',
      coin: 'bg-purple-100 text-purple-800',
      fiat: 'bg-blue-100 text-blue-800',
    };
    return badges[method as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  const filteredUsers = stats?.financial.filter(userData => {
    const matchesSearch =
      userData.user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userData.user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userData.user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPackage = filterPackage === 'all' ||
      userData.packages.some(pkg => pkg.packageId._id === filterPackage);

    return matchesSearch && matchesPackage;
  }) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading financial data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats?.financial) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">Failed to load financial data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Financial Overview</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Comprehensive financial data for {stats.totalUsers} users
            </p>
          </div>
          <button
            onClick={() => !stats}
            className="bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Global Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Balance</h3>
              <Wallet className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {formatCurrency(stats.globalTotals.totalBalance)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Deposits</h3>
              <ArrowDownRight className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(stats.globalTotals.totalDeposits)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Withdrawals</h3>
              <ArrowUpRight className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-600 dark:text-red-400">
              {formatCurrency(stats.globalTotals.totalWithdrawals)}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Referral Earnings</h3>
              <Gift className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {formatCurrency(stats.globalTotals.totalReferralEarnings)}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab('overview')
                  setCurrentPage(0);
                }}

                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview'
                  ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>Overview</span>
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 rounded-full">
                    {stats.packageTotals.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('packages')
                  setCurrentPage(1)

                }}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'packages'
                  ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Package className="h-4 w-4" />
                  <span>Package Analytics</span>
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 rounded-full">
                    {stats.packageTotals.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('users')
                  setCurrentPage(1);
                }}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users'
                  ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>User Financial Data</span>
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 rounded-full">
                    {stats.totalUsers}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Active Users</h3>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {/* {stats.packageTotals.reduce((sum, pkg) => sum + pkg.totalActiveUsers, 0)} */}
                      {stats?.totalActiveUsers}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">Total Saved</h3>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(stats.packageTotals.reduce((sum, pkg) => sum + pkg.totalSaved, 0))}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">Monthly ROI</h3>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(stats.packageTotals.reduce((sum, pkg) => sum + pkg.totalMonthlyROI, 0))}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Package Analytics Tab */}
            {activeTab === 'packages' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Package</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Total Saved</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Active Users</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Monthly ROI</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Monthly Return</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Referral Bonus</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {stats.packageTotals.map((packageData) => (
                      <tr key={packageData.packageId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">{packageData.packageName}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Min: {formatCurrency(packageData.minDeposit)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(packageData.totalSaved)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900 dark:text-white">{packageData.totalActiveUsers}</span>
                            <span className="text-gray-500 dark:text-gray-400">active</span>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {packageData.totalClosedUsers} closed
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-blue-600 dark:text-blue-400">
                            {formatCurrency(packageData.totalMonthlyROI)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatPercentage(packageData.monthlyReturn)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                            {formatPercentage(packageData.referralBonus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* User Financial Data Tab */}
            {activeTab === 'users' && (
              <>
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Filter className="h-5 w-5 text-gray-400" />
                    <select
                      value={filterPackage}
                      onChange={(e) => setFilterPackage(e.target.value)}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="all">All Packages</option>
                      {stats.packageTotals.map((pkg) => (
                        <option key={pkg.packageId} value={pkg.packageId}>
                          {pkg.packageName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">User</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Wallet Balance</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Total Deposits</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Active Packages</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Transactions</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {filteredUsers.map((userData) => (
                        <tr key={userData.user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {userData.user.firstName} {userData.user.lastName}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{userData.user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-semibold text-blue-600 dark:text-blue-400">
                              {formatCurrency(userData.wallet?.balance || 0)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(userData.wallet?.totalDeposits || 0)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {userData.packages.filter(pkg => pkg.status === 'active').length} active
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {userData.packages.filter(pkg => pkg.status === 'closed').length} closed
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="text-gray-900 dark:text-white">{userData.transactions.length} total</div>
                              <div className="text-gray-500 dark:text-gray-400">
                                {userData.transactions.filter(t => t.status === 'pending').length} pending
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                setSelectedUser(userData);
                                setShowUserModal(true);
                              }}
                              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* User Details Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedUser.user.firstName} {selectedUser.user.lastName} - Financial Details
                  </h3>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    <RefreshCw className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Information</h4>
                    <div className="space-y-2">
                      <div><span className="font-medium">Email:</span> {selectedUser.user.email}</div>
                      <div><span className="font-medium">Phone:</span> {selectedUser.user.phone}</div>
                      <div><span className="font-medium">Joined:</span> {new Date(selectedUser.user.createdAt).toLocaleDateString()}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Wallet Summary</h4>
                    {selectedUser.wallet ? (
                      <div className="space-y-2">
                        <div><span className="font-medium">Balance:</span> {formatCurrency(selectedUser.wallet.balance)}</div>
                        <div><span className="font-medium">Total Deposits:</span> {formatCurrency(selectedUser.wallet.totalDeposits)}</div>
                        <div><span className="font-medium">Total Withdrawals:</span> {formatCurrency(selectedUser.wallet.totalWithdrawals)}</div>
                        <div><span className="font-medium">Referral Earnings:</span> {formatCurrency(selectedUser.wallet.totalReferralEarnings)}</div>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">No wallet data available</p>
                    )}
                  </div>
                </div>

                {/* Packages */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">investment Packages ({selectedUser.packages.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Package</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Amount</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Monthly ROI</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Status</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {selectedUser.packages.map((pkg) => (
                          <tr key={pkg._id}>
                            <td className="px-4 py-2 font-medium">{pkg.packageId.name}</td>
                            <td className="px-4 py-2">{formatCurrency(pkg.amount)}</td>
                            <td className="px-4 py-2">{formatCurrency(pkg.monthlyROI)}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(pkg.status)}`}>
                                {pkg.status}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(pkg.paymentMethod)}`}>
                                {pkg.paymentMethod}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Transactions ({selectedUser.transactions.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Type</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Amount</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Status</th>
                          <th className="px-4 py-2 text-left text-gray-900 dark:text-white">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {selectedUser.transactions.slice(0, 5).map((transaction) => (
                          <tr key={transaction._id}>
                            <td className="px-4 py-2 font-medium">{transaction.type}</td>
                            <td className="px-4 py-2">{formatCurrency(transaction.amount)}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(transaction.status)}`}>
                                {transaction.status}
                              </span>
                            </td>
                            <td className="px-4 py-2">{new Date(transaction.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment Details */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Details ({selectedUser.paymentDetails.length})</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedUser.paymentDetails.map((payment) => (
                      <div key={payment._id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900 dark:text-white">{payment.account_name}</h5>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(payment.pay_type)}`}>
                            {payment.pay_type}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <div><span className="font-medium">Bank:</span> {payment.bank_name}</div>
                          <div><span className="font-medium">Account:</span> {payment.account_number}</div>
                          {payment.wallet_address && (
                            <div><span className="font-medium">Wallet:</span> {payment.wallet_address}</div>
                          )}
                          {payment.network && (
                            <div><span className="font-medium">Network:</span> {payment.network}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
